import type { MatrixData } from '../components/MatrixTable'

import { deleteScopedKey, readScopedString, readWaterJSON, writeScopedString, writeWaterJSON } from './waterStorage'

export type TemperatureStatus = 'critical_cold' | 'too_cold' | 'optimal' | 'too_warm' | 'critical_warm'

export type InterpolationResult = {
  sfr: number
  temperatureStatus: TemperatureStatus
  temperatureWarning: string
  temperatureWarningKey?: string
  temperatureWarningParams?: Record<string, string | number>
  clampedTemp: number | null
}

type SpeciesKey = 'global' | 'trout' | 'salmon' | 'tilapia' | 'shrimp'

type TemperatureProfile = {
  min: number
  max: number
  cautionLow: number
  cautionHigh: number
  context: string
}

type SpeciesMatrixDefinition = {
  matrix: MatrixData
  temperature: TemperatureProfile
}

const cloneMatrixData = (matrix: MatrixData): MatrixData => ({
  temperatures: [...matrix.temperatures],
  weights: [...matrix.weights],
  values: matrix.values.map(row => [...row])
})

const roundMatrixValue = (value: number) => Number(value.toFixed(2))

const buildMatrixWithWeightAdjustments = (
  matrix: MatrixData,
  resolveDelta: (weight: number) => number
): MatrixData => ({
  temperatures: [...matrix.temperatures],
  weights: [...matrix.weights],
  values: matrix.values.map((row, rowIndex) => {
    const weight = matrix.weights[rowIndex] ?? 0
    const delta = resolveDelta(weight)
    return row.map(value => roundMatrixValue(value + delta))
  })
})

const areMatricesEqual = (left: MatrixData, right: MatrixData) => JSON.stringify(left) === JSON.stringify(right)

const SALMONID_MATRIX: MatrixData = {
  temperatures: [9, 10, 11, 12, 13, 14, 15, 16, 17],
  weights: [0.1, 0.45, 1.1, 2.8, 6.5, 8, 13, 22, 36, 80, 150, 300, 500, 1000],
  values: [
    [4.9, 5.3, 5.7, 6.1, 6.7, 7.0, 7.8, 8.0, 8.1],
    [3.4, 3.8, 4.0, 4.2, 4.5, 5.0, 5.2, 5.5, 5.7],
    [2.8, 3.1, 3.3, 3.5, 3.8, 4.0, 4.3, 4.5, 4.6],
    [2.6, 2.7, 3.0, 3.2, 3.4, 3.5, 3.8, 4.0, 4.2],
    [2.5, 2.6, 2.9, 3.1, 3.3, 3.5, 3.7, 4.0, 4.2],
    [2.1, 2.2, 2.42, 2.6, 2.78, 3.0, 3.1, 3.6, 4.0],
    [1.87, 2.0, 2.16, 2.4, 2.55, 2.7, 2.8, 3.3, 3.5],
    [1.62, 1.7, 1.82, 1.9, 2.13, 2.2, 2.4, 2.8, 3.0],
    [1.29, 1.29, 1.5, 1.6, 1.8, 1.9, 2.0, 2.3, 2.8],
    [1.15, 1.15, 1.32, 1.4, 1.5, 1.6, 1.7, 1.9, 2.0],
    [1.0, 1.0, 1.18, 1.2, 1.32, 1.4, 1.5, 1.6, 2.0],
    [1.0, 1.0, 1.2, 1.2, 1.4, 1.4, 1.4, 1.5, 1.6],
    [0.9, 0.9, 1.1, 1.1, 1.2, 1.3, 1.3, 1.4, 1.4],
    [0.6, 0.6, 0.6, 0.6, 0.6, 0.7, 0.7, 0.8, 0.8]
  ]
}

const SALMON_MATRIX = buildMatrixWithWeightAdjustments(SALMONID_MATRIX, (weight) => {
  if (weight <= 2.8) return 0.2
  if (weight <= 80) return 0.1
  return 0.05
})

const LEGACY_SALMON_MATRIX = buildMatrixWithWeightAdjustments(SALMONID_MATRIX, (weight) => {
  if (weight <= 2.8) return 0.2
  if (weight <= 80) return 0.1
  return 0.5
})

const TILAPIA_MATRIX: MatrixData = {
  temperatures: [16, 17, 18, 19, 20, 21, 22],
  weights: [0.1, 0.45, 1.1, 2.8, 6.5, 8, 13, 22, 36, 80, 150, 300, 500, 1000],
  values: [
    [7.5, 8.5, 8.5, 8.7, 9.0, 9.4, 9.6],
    [5.2, 6.2, 6.3, 6.8, 7.2, 7.5, 7.8],
    [5.0, 6.0, 6.2, 6.6, 7.0, 7.1, 7.5],
    [4.3, 4.8, 4.8, 5.3, 5.8, 6.0, 6.5],
    [3.9, 4.1, 4.6, 4.9, 5.1, 5.3, 5.6],
    [3.6, 3.9, 4.3, 4.6, 4.9, 5.0, 5.3],
    [3.2, 3.5, 3.8, 4.2, 4.5, 4.7, 5.1],
    [2.9, 3.1, 3.6, 3.9, 4.1, 4.1, 4.4],
    [2.5, 2.8, 3.1, 3.5, 3.8, 3.9, 4.2],
    [2.2, 2.6, 2.8, 3.2, 3.6, 4.0, 4.1],
    [1.9, 2.2, 2.5, 2.9, 3.2, 3.5, 4.0],
    [1.6, 1.8, 2.4, 2.6, 2.8, 3.0, 3.3],
    [1.5, 1.6, 2.3, 2.5, 2.6, 2.9, 3.1],
    [0.9, 1.0, 1.8, 1.9, 2.0, 2.2, 2.8]
  ]
}

// Pacific White Shrimp (PWS) default editable matrix.
// Source: user-provided table (weights in grams; temperatures in °C; values are SFR %).
const SHRIMP_PWS_MATRIX: MatrixData = {
  temperatures: [27, 32],
  weights: [0.002, 0.1, 0.5, 1, 3, 5, 10],
  values: [
    [50, 200],
    [30, 40],
    [18, 20],
    [15, 16],
    [6, 10],
    [2.6, 7.5],
    [1.3, 4.6]
  ]
}

const SPECIES_DEFINITIONS: Record<SpeciesKey, SpeciesMatrixDefinition> = {
  global: {
    matrix: SALMONID_MATRIX,
    temperature: {
      min: 7,
      cautionLow: 9,
      cautionHigh: 17,
      max: 19,
      context: 'coldwater salmonids'
    }
  },
  trout: {
    matrix: SALMONID_MATRIX,
    temperature: {
      min: 7,
      cautionLow: 9,
      cautionHigh: 17,
      max: 19,
      context: 'rainbow trout'
    }
  },
  salmon: {
    matrix: SALMON_MATRIX,
    temperature: {
      min: 7,
      cautionLow: 9,
      cautionHigh: 16,
      max: 18,
      context: 'Atlantic salmon'
    }
  },
  tilapia: {
    matrix: TILAPIA_MATRIX,
    temperature: {
      min: 15,
      cautionLow: 16,
      cautionHigh: 21,
      max: 22,
      context: 'warmwater tilapia'
    }
  },
  shrimp: {
    matrix: SHRIMP_PWS_MATRIX,
    temperature: {
      min: 20,
      cautionLow: 24,
      cautionHigh: 34,
      max: 38,
      context: 'Pacific White Shrimp'
    }
  }
}

const SPECIES_ALIASES: Record<string, SpeciesKey> = {
  global: 'global',
  trout: 'trout',
  salmon: 'salmon',
  tilapia: 'tilapia',
  shrimp: 'shrimp',
  'rainbow trout': 'trout',
  'atlantic salmon': 'salmon',
  'nile tilapia': 'tilapia',
  'pacific white shrimp': 'shrimp',
  pws: 'shrimp'
}

const resolveSpeciesKey = (input?: string): SpeciesKey => {
  if (!input) return 'global'
  const normalized = input.trim().toLowerCase()
  if (normalized in SPECIES_DEFINITIONS) {
    return normalized as SpeciesKey
  }
  return SPECIES_ALIASES[normalized] ?? 'global'
}

export const resolveSfrMatrixPresetKey = (species?: string): SpeciesKey => {
  return resolveSpeciesKey(species)
}

const normalizeCachedMatrixForSpecies = (species: string, matrix: MatrixData): MatrixData => {
  if (resolveSpeciesKey(species) === 'salmon') {
    if (areMatricesEqual(matrix, SALMONID_MATRIX) || areMatricesEqual(matrix, LEGACY_SALMON_MATRIX)) {
      return cloneMatrixData(SALMON_MATRIX)
    }
  }

  return cloneMatrixData(matrix)
}

export const AVAILABLE_SFR_SPECIES = Object.keys(SPECIES_DEFINITIONS)

export const SFR_STORAGE_KEY_PREFIX = 'sfrMatrixData'
const SFR_LOCAL_EDIT_MARKER_PREFIX = 'sfrMatrixLocalEdit'
const LOCAL_EDIT_MAX_AGE_MS = 5 * 60 * 1000

export const DEFAULT_SFR_MATRIX: MatrixData = cloneMatrixData(SPECIES_DEFINITIONS.global.matrix)

export const getStorageKeyForSpecies = (species: string) => `${SFR_STORAGE_KEY_PREFIX}:${resolveSfrMatrixPresetKey(species)}`

const getLocalEditMarkerKeyForSpecies = (species: string) => `${SFR_LOCAL_EDIT_MARKER_PREFIX}:${resolveSfrMatrixPresetKey(species)}`

const getLegacyMatrixStorageKeys = (species: string): string[] => {
  const presetKey = resolveSfrMatrixPresetKey(species)
  const keys = new Set<string>([
    getStorageKeyForSpecies(presetKey),
    `${SFR_STORAGE_KEY_PREFIX}:${species}`
  ])

  if (presetKey === 'global') {
    keys.add(SFR_STORAGE_KEY_PREFIX)
  }

  return Array.from(keys)
}

export const cloneMatrix = (matrix: MatrixData): MatrixData => cloneMatrixData(matrix)

export const getDefaultMatrixForSpecies = (species: string): MatrixData => {
  const key = resolveSpeciesKey(species)
  const base = SPECIES_DEFINITIONS[key]?.matrix ?? SPECIES_DEFINITIONS.global.matrix
  return cloneMatrixData(base)
}

export const interpolateSfr = (
  weight: number,
  temperature: number,
  options: {
    species?: string
    matrix?: MatrixData | null
  } = {}
): InterpolationResult => {
  const speciesKey = resolveSpeciesKey(options.species)
  const matrix = options.matrix ?? getDefaultMatrixForSpecies(speciesKey)
  return interpolateFromMatrix(matrix, weight, temperature, { species: speciesKey })
}

export const loadCachedMatrix = (species: string): MatrixData | null => {
  try {
    const primaryKey = getStorageKeyForSpecies(species)
    const candidates = getLegacyMatrixStorageKeys(species)

    let sourceKey: string | null = null
    let parsed: MatrixData | null = null
    for (const candidate of candidates) {
      const value = readWaterJSON<MatrixData | null>(candidate, null)
      if (value) {
        sourceKey = candidate
        parsed = value
        break
      }
    }

    if (!parsed) return null

    const defaults = getDefaultMatrixForSpecies(species)
    const normalizedMatrix = {
      temperatures: Array.isArray(parsed.temperatures) ? parsed.temperatures : defaults.temperatures,
      weights: Array.isArray(parsed.weights) ? parsed.weights : defaults.weights,
      values: Array.isArray(parsed.values) ? parsed.values.map(row => [...row]) : defaults.values
    }

    const cachedMatrix = normalizeCachedMatrixForSpecies(species, normalizedMatrix)

    if ((sourceKey && sourceKey !== primaryKey) || !areMatricesEqual(cachedMatrix, normalizedMatrix)) {
      cacheMatrix(species, cachedMatrix)
    }

    return cachedMatrix
  } catch (error) {
    console.warn('Failed to read cached SFR matrix', error)
    return null
  }
}

export const cacheMatrix = (species: string, matrix: MatrixData) => {
  try {
    writeWaterJSON(getStorageKeyForSpecies(species), matrix)
  } catch (error) {
    console.warn('Failed to cache SFR matrix', error)
  }
}

export const markLocalSfrMatrixEdit = (species: string) => {
  try {
    writeScopedString(getLocalEditMarkerKeyForSpecies(species), new Date().toISOString())
  } catch (error) {
    console.warn('Failed to mark local SFR matrix edit', error)
  }
}

export const clearLocalSfrMatrixEdit = (species: string) => {
  deleteScopedKey(getLocalEditMarkerKeyForSpecies(species))
}

export const hasRecentLocalSfrMatrixEdit = (species: string): boolean => {
  const raw = readScopedString(getLocalEditMarkerKeyForSpecies(species))
  if (!raw) {
    return false
  }

  const parsed = Date.parse(raw)
  if (!Number.isFinite(parsed)) {
    clearLocalSfrMatrixEdit(species)
    return false
  }

  const isRecent = Date.now() - parsed <= LOCAL_EDIT_MAX_AGE_MS
  if (!isRecent) {
    clearLocalSfrMatrixEdit(species)
  }

  return isRecent
}

export const clearCachedMatrix = (species?: string) => {
  if (species) {
    const presetKey = resolveSfrMatrixPresetKey(species)
    if (presetKey === 'global') deleteScopedKey('sfrMatrixData')
    deleteScopedKey(getStorageKeyForSpecies(presetKey))
    clearLocalSfrMatrixEdit(presetKey)
    return
  }

  deleteScopedKey('sfrMatrixData')
  ;(['global', 'trout', 'salmon', 'tilapia', 'shrimp'] satisfies SpeciesKey[]).forEach((key) => {
    deleteScopedKey(getStorageKeyForSpecies(key))
    clearLocalSfrMatrixEdit(key)
  })
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const findBoundingIndexes = (values: number[], target: number): [number, number] => {
  if (values.length === 0) return [0, 0]
  if (target <= values[0]) return [0, 0]
  const lastIndex = values.length - 1
  if (target >= values[lastIndex]) return [lastIndex, lastIndex]
  for (let i = 0; i < lastIndex; i += 1) {
    if (target <= values[i + 1]) {
      return [i, i + 1]
    }
  }
  return [lastIndex, lastIndex]
}

const assessTemperature = (
  temperature: number,
  matrixTemperatures: number[],
  profile: TemperatureProfile
): {
  clamped: number
  status: TemperatureStatus
  warning: string
  warningKey?: string
  warningParams?: Record<string, string | number>
} => {
  const matrixMin = matrixTemperatures[0]
  const matrixMax = matrixTemperatures[matrixTemperatures.length - 1]
  const effectiveMin = Math.max(profile.min, matrixMin)
  const effectiveMax = Math.min(profile.max, matrixMax)
  const cautionLow = Math.max(profile.cautionLow, profile.min)
  const cautionHigh = Math.min(profile.cautionHigh, profile.max)

  let clamped = temperature
  let status: TemperatureStatus = 'optimal'
  let warning = ''
  let warningKey: string | undefined
  let warningParams: Record<string, string | number> | undefined

  if (temperature < profile.min) {
    status = 'critical_cold'
    warning = `🚨 CRITICAL: Temperature below ${profile.min}°C - suspend feeding for ${profile.context}. Values are clamped to ${effectiveMin}°C for interpolation.`
    warningKey = 'feed.temperatureWarning.criticalCold'
    warningParams = { min: profile.min, clamp: effectiveMin, context: profile.context }
    clamped = effectiveMin
  } else if (temperature <= cautionLow) {
    status = 'too_cold'
    warning = `❄️ WARNING: Temperature is near the lower threshold for ${profile.context}. Monitor dissolved oxygen closely before feeding.`
    warningKey = 'feed.temperatureWarning.tooCold'
    warningParams = { context: profile.context }
  } else if (temperature >= profile.max) {
    status = 'critical_warm'
    warning = `🚨 CRITICAL: Temperature above ${profile.max}°C - suspend feeding for ${profile.context}. Values are clamped to ${effectiveMax}°C for interpolation.`
    warningKey = 'feed.temperatureWarning.criticalWarm'
    warningParams = { max: profile.max, clamp: effectiveMax, context: profile.context }
    clamped = effectiveMax
  } else if (temperature >= cautionHigh) {
    status = 'too_warm'
    warning = `🔥 WARNING: Temperature is approaching the upper tolerance for ${profile.context}. Reduce feeding rates and monitor water quality.`
    warningKey = 'feed.temperatureWarning.tooWarm'
    warningParams = { context: profile.context }
  }

  if (clamped < matrixMin) {
    clamped = matrixMin
  } else if (clamped > matrixMax) {
    clamped = matrixMax
  }

  return { clamped, status, warning, warningKey, warningParams }
}

export const lookupSfrValue = (matrix: MatrixData, weight: number, temperature: number): number | null => {
  const weightIndex = matrix.weights.findIndex(w => w >= weight)
  const resolvedWeightIndex = weightIndex === -1 ? matrix.weights.length - 1 : Math.max(0, weightIndex - 1)

  const tempIndex = matrix.temperatures.findIndex(t => t >= temperature)
  const resolvedTempIndex = tempIndex === -1 ? matrix.temperatures.length - 1 : Math.max(0, tempIndex - 1)

  return matrix.values[resolvedWeightIndex]?.[resolvedTempIndex] ?? null
}

export const interpolateFromMatrix = (
  matrix: MatrixData,
  weight: number,
  temperature: number,
  options?: { species?: string }
): InterpolationResult => {
  const weights = matrix.weights
  const temperatures = matrix.temperatures
  const values = matrix.values

  if (weights.length === 0 || temperatures.length === 0 || values.length === 0) {
    return {
      sfr: 0,
      temperatureStatus: 'optimal',
      temperatureWarning: '',
      clampedTemp: null
    }
  }

  const speciesKey = resolveSpeciesKey(options?.species)
  const profile = SPECIES_DEFINITIONS[speciesKey]?.temperature ?? SPECIES_DEFINITIONS.global.temperature

  const { clamped, status, warning, warningKey, warningParams } = assessTemperature(temperature, temperatures, profile)
  const clampedWeight = clamp(weight, weights[0], weights[weights.length - 1])
  const clampedForMatrix = clamp(clamped, temperatures[0], temperatures[temperatures.length - 1])

  const [weightLowerIndex, weightUpperIndex] = findBoundingIndexes(weights, clampedWeight)
  const [tempLowerIndex, tempUpperIndex] = findBoundingIndexes(temperatures, clampedForMatrix)

  const w1 = weights[weightLowerIndex]
  const w2 = weights[weightUpperIndex]
  const t1 = temperatures[tempLowerIndex]
  const t2 = temperatures[tempUpperIndex]

  const v11 = values[weightLowerIndex]?.[tempLowerIndex] ?? 0
  const v12 = values[weightLowerIndex]?.[tempUpperIndex] ?? v11
  const v21 = values[weightUpperIndex]?.[tempLowerIndex] ?? v11
  const v22 = values[weightUpperIndex]?.[tempUpperIndex] ?? v21

  let sfr = v11

  if (w2 === w1 && t2 === t1) {
    sfr = v11
  } else if (w2 === w1) {
    const ratio = t2 === t1 ? 0 : (clampedForMatrix - t1) / (t2 - t1)
    sfr = v11 + ratio * (v12 - v11)
  } else if (t2 === t1) {
    const ratio = w2 === w1 ? 0 : (clampedWeight - w1) / (w2 - w1)
    sfr = v11 + ratio * (v21 - v11)
  } else {
    const wRatio = (clampedWeight - w1) / (w2 - w1)
    const tRatio = (clampedForMatrix - t1) / (t2 - t1)
    const interp1 = v11 + wRatio * (v21 - v11)
    const interp2 = v12 + wRatio * (v22 - v12)
    sfr = interp1 + tRatio * (interp2 - interp1)
  }

  return {
    sfr,
    temperatureStatus: status,
    temperatureWarning: warning,
    temperatureWarningKey: warningKey,
    temperatureWarningParams: warningParams,
    clampedTemp: clamped !== temperature ? clamped : null
  }
}
